import { useId, useState } from 'react';
import { LigneFichier, type TonPastille } from '../ui/Primitives';
import { PASTILLE_PIECE } from '../../lib/pastilles';
import type { StatutPiece } from '../../lib/useApprenants';
import { Icone } from '../Icone';

/**
 * Ligne d'une pièce justificative, avec dépôt, retrait et contrôle.
 *
 * Partagée entre le dossier tenu par le pôle administratif (`AdmDossierPage`)
 * et l'espace personnel du candidat (`AppDocumentsPage`) : c'est la même
 * mécanique de dépôt des deux côtés, l'API acceptant les deux écrans et
 * vérifiant elle-même que le candidat ne touche qu'à son propre dossier.
 */

/** Un fichier déposé pour une pièce : forme commune aux deux écrans. */
export interface FichierPieceCommun {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  deposeLe: string;
}

/** Pièce justificative telle que la portent les deux écrans qui l'affichent. */
export interface PieceAvecFichiers {
  libelle: string;
  obligatoire: boolean;
  statut: StatutPiece;
  motifRefus: string | null;
  deposeeLe: string | null;
  nombreAttendu: number;
  nombreMaximum: number;
  formatsAcceptes: string[];
  libelleFormats: string;
  tailleMaxOctets: number;
  fichiers: FichierPieceCommun[];
}

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function dateCourte(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Poids d'un fichier, arrondi comme l'affiche un explorateur de fichiers. */
function poidsLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
}

export function LignePiece<P extends PieceAvecFichiers>({
  piece,
  onValider,
  onVerifier,
  onDeposer,
  onSupprimerFichier,
  onConsulter,
  onRefuser,
  enCours,
  gestionAdministrative,
  consultationSeule,
  ouvrirFichiers = !consultationSeule,
}: {
  piece: P;
  onValider: () => void;
  onVerifier: () => void;
  onDeposer: (fichier: File, remplace?: string) => void;
  onSupprimerFichier: (fichierId: string) => void;
  /** Ouvre la visionneuse sur le fichier de ce rang. */
  onConsulter: (index: number) => void;
  onRefuser: () => void;
  enCours: boolean;
  gestionAdministrative: boolean;
  /** Lecture seule d'un autre pôle : ni dépôt, ni retrait. */
  consultationSeule: boolean;
  /**
   * Les fichiers s'ouvrent-ils dans la visionneuse ? Décidé par l'API
   * (`accesPieces`) : le pôle facturation les consulte sans rien déposer
   * (TF-464). Par défaut, tout ce qui n'est pas une consultation.
   */
  ouvrirFichiers?: boolean;
}): JSX.Element {
  const deposes = piece.fichiers.length;
  const complet = deposes >= piece.nombreAttendu;

  // Les contraintes de dépôt sont affichées avant le dépôt, pas découvertes
  // dans un message d'erreur : elles viennent de l'API, qui les impose.
  const contrainte = `${piece.libelleFormats} · ${Math.round(piece.tailleMaxOctets / 1024 / 1024)} Mo max`;

  const detail = (() => {
    if (piece.statut === 'REFUSEE') return `${piece.motifRefus ?? 'Refusée'} · ${contrainte}`;
    if (deposes === 0) {
      const attente = piece.obligatoire ? 'À déposer par le candidat' : 'À déposer si concerné';
      const combien = piece.nombreAttendu > 1 ? ` · ${piece.nombreAttendu} fichiers attendus` : '';
      return `${attente}${combien} · ${contrainte}`;
    }
    const compte =
      piece.nombreAttendu > 1
        ? `${deposes}/${piece.nombreAttendu} fichiers déposés`
        : `${deposes} fichier déposé`;
    const quand = piece.deposeeLe ? ` le ${dateCourte(piece.deposeeLe)}` : '';
    return `${compte}${quand} · ${contrainte}`;
  })();

  // Une pièce partiellement déposée n'est ni « non déposée » ni « à contrôler » :
  // une seule des deux photos d'identité laisse la pièce en attente.
  const pastille =
    !complet && deposes > 0 && piece.statut === 'ATTENDUE'
      ? { texte: `Incomplet ${deposes}/${piece.nombreAttendu}`, ton: 'warning' as TonPastille }
      : PASTILLE_PIECE[piece.statut];

  // Le contrôle n'a de sens qu'une fois le fichier là, et il est réservé aux
  // pôles : le candidat ne valide pas ses propres pièces.
  const aControler = gestionAdministrative && (piece.statut === 'DEPOSEE' || piece.statut === 'VERIFIEE');
  // Le dépôt reste ouvert tant que la pièce n'est pas validée : une pièce
  // refusée doit pouvoir être remplacée sans repasser par le secrétariat. Il
  // se ferme aussi quand le nombre maximum de fichiers est atteint.
  //
  // Il est ouvert aux deux côtés, et pas seulement à l'espace candidat : une
  // pièce arrive souvent au guichet ou par courrier, et c'est le secrétariat
  // qui la numérise. L'API l'autorise explicitement — la route de dépôt est
  // gardée par `exigerUnDesEcrans('app-documents', 'adm-dossier')`.
  //
  // Une pièce vérifiée est close au même titre qu'une pièce validée : la
  // vérification porte sur le fichier déposé, le remplacer sans repasser par
  // « Valider » ou « Refuser » la viderait de son sens. L'API refuse le
  // dépôt dans ce cas, le bouton disparaît donc aussi ici.
  const aDeposer = !consultationSeule && piece.statut !== 'VALIDEE' && piece.statut !== 'VERIFIEE';
  // Pièce au complet : le dépôt remplace le dernier fichier plutôt que de
  // s'empiler. Le seuil est le nombre **attendu**, pas le maximum accepté par
  // l'API : une pièce qui ne demande qu'un fichier doit se corriger d'un geste,
  // sans que le précédent reste à côté du nouveau.
  const remplace = complet ? piece.fichiers[deposes - 1]?.id : undefined;
  // Une pièce validée ou vérifiée est close : l'API refuse le retrait, le
  // bouton disparaît.
  const aRetirer = !consultationSeule && piece.statut !== 'VALIDEE' && piece.statut !== 'VERIFIEE';

  return (
    <div>
      <LigneFichier
        nom={piece.libelle + (piece.obligatoire ? '' : ' (si concerné)')}
        detail={detail}
        pastille={pastille}
        actions={
          aDeposer || aControler ? (
            // `flex-wrap` : sur tablette, trois actions et la pastille ne tiennent
            // pas toujours sur la ligne à côté d'un libellé de pièce long.
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
              {aDeposer && (
                <BoutonDepot
                  libelle={deposes === 0 ? 'Déposer' : remplace ? 'Remplacer' : 'Ajouter'}
                  piece={piece}
                  enCours={enCours}
                  onFichier={(fichier) => onDeposer(fichier, remplace)}
                />
              )}
              {aControler && piece.statut === 'DEPOSEE' && (
                <button
                  type="button"
                  onClick={onVerifier}
                  disabled={enCours}
                  className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-gray-200 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50"
                >
                  Vérifier
                </button>
              )}
              {aControler && (
                <button
                  type="button"
                  onClick={onValider}
                  disabled={enCours}
                  className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg bg-codialis-mint/20 px-3 py-2 text-[11px] font-semibold text-codialis-accent-texte transition hover:bg-codialis-mint/30 disabled:opacity-50"
                >
                  Valider
                </button>
              )}
              {/* Refuser exige un motif, que le candidat lira dans son espace :
                  sans lui, il redéposerait le même document. La saisie se fait
                  donc dans une fenêtre, pas d'un clic. */}
              {aControler && (
                <button
                  type="button"
                  onClick={onRefuser}
                  disabled={enCours}
                  className="min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-red-200 px-3 py-2 text-[11px] font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                >
                  Refuser
                </button>
              )}
            </div>
          ) : null
        }
      />

      {deposes > 0 && (
        // Une pièce peut porter plusieurs fichiers : ils sont listés sous elle,
        // chacun retirable tant que la pièce n'est pas validée. En pile sur
        // mobile, en ligne dès que la largeur le permet.
        <ul className="space-y-1 px-4 sm:px-6 pb-3 sm:pl-[3.25rem]">
          {piece.fichiers.map((fichier, rang) => (
            <li
              key={fichier.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-codialis-sand px-3 py-2"
            >
              {/* Le nom ouvre le fichier : c'est le geste attendu pour
                  vérifier une pièce. Un vrai bouton, pas un `span` cliquable,
                  pour rester atteignable au clavier et annoncé correctement.
                  Sans droit d'ouverture, le fichier lui-même reste fermé — l'API
                  garde sa route, le nom s'affiche sans être cliquable. */}
              {!ouvrirFichiers ? (
                <span className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] min-w-0 flex-1 items-center gap-2 px-1 text-xs font-medium text-codialis-navy">
                  <Icone nom="document" className="h-5 w-5 shrink-0 text-codialis-gray" />
                  <span className="truncate">{fichier.nomFichier}</span>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onConsulter(rang)}
                  className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] min-w-0 flex-1 items-center gap-2 rounded-lg px-1 text-left text-xs font-medium text-codialis-navy transition hover:text-codialis-sky hover:underline focus-visible:ring-2 focus-visible:ring-codialis-sky"
                  aria-label={`Ouvrir ${fichier.nomFichier}`}
                >
                  <Icone nom="oeil" className="h-5 w-5 shrink-0 text-codialis-gray" />
                  <span className="truncate">{fichier.nomFichier}</span>
                </button>
              )}
              <span className="text-[11px] text-codialis-gray">
                {poidsLisible(fichier.tailleOctets)} · déposé le {dateCourte(fichier.deposeLe)}
              </span>
              {aRetirer && (
                <button
                  type="button"
                  onClick={() => onSupprimerFichier(fichier.id)}
                  disabled={enCours}
                  // 38 px de haut et de large : cible tactile confortable sur
                  // tablette, où le dossier est surtout consulté.
                  className="inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] min-w-[38px] [@media(pointer:coarse)]:min-w-[40px] items-center justify-center rounded-lg border border-transparent text-codialis-gray transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-codialis-sky disabled:opacity-50"
                  aria-label={`Retirer le fichier ${fichier.nomFichier}`}
                >
                  <Icone nom="corbeille" className="h-5 w-5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Dépôt d'un fichier, par le candidat depuis son espace comme par le
 * secrétariat depuis le dossier.
 *
 * Un `label` associé à un `input file` masqué : la zone reste un vrai contrôle
 * de formulaire, atteignable au clavier et annoncé aux lecteurs d'écran, tout
 * en gardant l'allure d'un bouton. La hauteur suit celle des autres actions,
 * assez large pour le doigt sur tablette.
 *
 * Le format et la taille sont revérifiés côté API — type MIME, extension et
 * signature binaire du contenu. Ce contrôle-ci n'est qu'un confort : il évite
 * d'attendre le téléversement d'un fichier qui sera refusé.
 */
function BoutonDepot({
  libelle,
  piece,
  enCours,
  onFichier,
}: {
  libelle: string;
  piece: PieceAvecFichiers;
  enCours: boolean;
  onFichier: (fichier: File) => void;
}): JSX.Element {
  const champ = useId();
  const [refus, setRefus] = useState<string | null>(null);

  return (
    <div className="flex flex-col items-end gap-1">
      <label
        htmlFor={champ}
        title={`Formats acceptés : ${piece.libelleFormats}`}
        className={`inline-flex min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] cursor-pointer items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-codialis-sky ${
          enCours ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        <Icone nom="televerser" className="h-3.5 w-3.5" />
        {libelle}
        <input
          id={champ}
          type="file"
          className="sr-only"
          accept={piece.formatsAcceptes.join(',')}
          disabled={enCours}
          onChange={(evenement) => {
            const fichier = evenement.target.files?.[0];
            // Le champ est remis à zéro pour que redéposer le même fichier
            // après un refus déclenche bien un nouvel événement.
            evenement.target.value = '';
            if (!fichier) return;
            if (fichier.size > piece.tailleMaxOctets) {
              setRefus(
                `Fichier trop lourd : ${Math.round(piece.tailleMaxOctets / 1024 / 1024)} Mo au maximum.`,
              );
              return;
            }
            // Certains navigateurs laissent choisir un fichier hors `accept`
            // (glisser-déposer, sélecteur « tous les fichiers ») : le format
            // est donc revérifié ici, avant l'envoi.
            if (fichier.type && !piece.formatsAcceptes.includes(fichier.type)) {
              setRefus(`Format refusé. Formats acceptés : ${piece.libelleFormats}.`);
              return;
            }
            setRefus(null);
            onFichier(fichier);
          }}
        />
      </label>
      {refus && (
        <span role="alert" className="max-w-[16rem] text-right text-[11px] text-red-600">
          {refus}
        </span>
      )}
    </div>
  );
}
